/**
 * SpiralCosmicScene: Fullwidth Grok's Black Hole WebGL Shader
 *
 * Choreographed multi-stage scroll sequence:
 * 1. Tip Corner Rays: Blue ray starts at bottom-left corner tip, red ray starts at top-right corner tip
 * 2. Slowly Forms Circle: On scroll, rays stream inward along logarithmic spirals to form a pure circle
 * 3. Pure Glowing White Circle (No Grok logo): As circle closes, turns into a brilliant white circular ring framing the images
 * 4. Images Emerge from Center: Spiral images scale up from the center inside the white circle
 * 5. White Part Covers Outer Part: While scrolling through the 15 images, white aura expands to outer canvas
 * 6. Inner Dark Void Increases: On final scroll after last image, inner dark void expands outward
 */
export class SpiralCosmicScene {
  constructor(canvasId = 'spiral-cosmic-canvas') {
    this.canvas = typeof canvasId === 'string' ? document.getElementById(canvasId) : canvasId;
    if (!this.canvas) return;

    this.gl = this.canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: 'high-performance',
    });

    if (!this.gl) {
      console.warn('WebGL not supported for SpiralCosmicScene');
      return;
    }

    this.isVisible = false;
    this.animationFrameId = null;
    this.startTime =
      typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now();
    this.boundResize = () => this.resize();

    // Scroll state parameters driven by ScrollTrigger
    this.rayProgress = 0.0;
    this.whiteIntensity = 0.0;
    this.inwardProgress = 0.0;
    this.whiteSpread = 0.0;
    this.darkCore = 0.0;
    this.scrollSpin = 0.0;

    this.init();
  }

  init() {
    const gl = this.gl;

    // 1. Vertex Shader: Pass-through quad coordinates
    const vsSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // 2. Fragment Shader: Seamless Orbiting Rays -> Scroll-Driven Inward Vortex -> Thanos Dust Disintegration
    const fsSource = `
      precision mediump float;
      uniform float t;
      uniform vec2 r;                   // resolution (width, height)
      uniform vec2 u_center;            // stage center in canvas pixels
      uniform float u_scale;            // circle scale (frames card images)
      uniform float u_ray_progress;     // 0.0 to 1.0: corner tip rays stream in and circle around to complete circle
      uniform float u_white_intensity;  // 0.0 to 1.0: pure white circle ignites as circle forms
      uniform float u_inward_progress;  // 0.0 to 1.0: blue & red colors move inside circle, spiraling toward center
      uniform float u_white_spread;     // 0.0 to 1.0: white glow expands outward covering outer canvas
      uniform float u_dark_core;        // 0.0 to 1.0: inner tiny black void inhales white part, revealing background grid
      uniform float u_scroll_spin;      // Scroll-driven rotation angle (matches scroll up or down)

      void main() {
        // Centered normalized coordinates (Y: -1 to 1, X: -aspect to +aspect)
        vec2 p = (gl_FragCoord.xy - u_center) * 2.0 / r.y;
        vec2 p_scaled = p / u_scale;

        float r_len = length(p_scaled);
        float phi = atan(p_scaled.y, p_scaled.x); // -PI to +PI
        float aspect = r.x / r.y;

        float R_circle = 0.72; // Circular stage radius
        float R_corner = length(vec2(aspect, 1.0)); // Distance to canvas corners

        // -------------------------------------------------------------
        // 1. Blue (Clockwise) & Red (Anticlockwise) Rays Circle Around with ZERO Crack
        // -------------------------------------------------------------
        float phi_BL_corner = atan(-1.0, -aspect);
        float phi_TR_corner = atan(1.0, aspect);
        float spiral_k = 1.35; // Curvature rate from corners to circle perimeter

        // Blue Ray (Bottom-Left): Streams in and wraps CLOCKWISE along circle
        float phi_BL_contact = phi_BL_corner + spiral_k * log(R_circle / R_corner);
        float s_blue = clamp((R_corner - r_len) / max(R_corner - R_circle, 0.001), 0.0, 1.0);
        float phi_exp_blue = phi_BL_corner + spiral_k * log(max(max(r_len, R_circle) / R_corner, 0.001));
        float d_phi_stream_blue = abs(mod(phi - phi_exp_blue + 3.14159265, 6.2831853) - 3.14159265);
        float wave_blue = sin(r_len * 10.0 - u_scroll_spin * 0.4) * 0.025;
        float dist_stream_blue = r_len * abs(d_phi_stream_blue + wave_blue);

        // Circular orbit clockwise from phi_BL_contact
        float d_cw = mod(phi_BL_contact - phi + 6.2831853, 6.2831853);
        float wrap_p_blue = smoothstep(0.35, 1.0, u_ray_progress);
        float reach_arc_blue = wrap_p_blue * 3.45; // Overlaps to close circle without seam
        float head_blue_ring = smoothstep(reach_arc_blue + 0.25, reach_arc_blue - 0.06, d_cw);
        float dist_ring = abs(r_len - R_circle);

        float w_stream_blue = 0.045 + 0.05 * s_blue;
        float reach_blue = clamp(u_ray_progress * 1.5, 0.0, 1.0);
        float head_stream_blue = smoothstep(reach_blue + 0.08, reach_blue - 0.04, s_blue);

        // Streams ONLY exist outside the circle and fade out once the circle completes (ZERO line inside circle)
        float isOutsideRing = smoothstep(R_circle - 0.015, R_circle + 0.035, r_len);
        float streamTailFade = 1.0 - smoothstep(0.60, 0.95, u_ray_progress);

        float intensity_stream_blue = exp(-pow(dist_stream_blue / w_stream_blue, 2.0)) * head_stream_blue * isOutsideRing * streamTailFade;
        float ring_intensity_blue = exp(-pow(dist_ring / 0.048, 2.0)) * head_blue_ring * smoothstep(0.35, 0.60, u_ray_progress);
        float total_ray_blue = max(intensity_stream_blue, ring_intensity_blue);

        // Red Ray (Top-Right): Streams in and wraps ANTICLOCKWISE along circle
        float phi_TR_contact = phi_TR_corner - spiral_k * log(R_circle / R_corner);
        float s_red = clamp((R_corner - r_len) / max(R_corner - R_circle, 0.001), 0.0, 1.0);
        float phi_exp_red = phi_TR_corner - spiral_k * log(max(max(r_len, R_circle) / R_corner, 0.001));
        float d_phi_stream_red = abs(mod(phi - phi_exp_red + 3.14159265, 6.2831853) - 3.14159265);
        float wave_red = sin(r_len * 10.0 - u_scroll_spin * 0.4 + 3.14159265) * 0.025;
        float dist_stream_red = r_len * abs(d_phi_stream_red + wave_red);

        // Circular orbit anticlockwise from phi_TR_contact
        float d_ccw = mod(phi - phi_TR_contact + 6.2831853, 6.2831853);
        float wrap_p_red = smoothstep(0.35, 1.0, u_ray_progress);
        float reach_arc_red = wrap_p_red * 3.45;
        float head_red_ring = smoothstep(reach_arc_red + 0.25, reach_arc_red - 0.06, d_ccw);

        float w_stream_red = 0.045 + 0.05 * s_red;
        float reach_red = clamp(u_ray_progress * 1.5, 0.0, 1.0);
        float head_stream_red = smoothstep(reach_red + 0.08, reach_red - 0.04, s_red);

        float intensity_stream_red = exp(-pow(dist_stream_red / w_stream_red, 2.0)) * head_stream_red * isOutsideRing * streamTailFade;
        float ring_intensity_red = exp(-pow(dist_ring / 0.048, 2.0)) * head_red_ring * smoothstep(0.35, 0.60, u_ray_progress);
        float total_ray_red = max(intensity_stream_red, ring_intensity_red);

        vec3 col_blue_ray = vec3(0.14, 0.70, 1.0) * total_ray_blue * 2.4;
        vec3 col_red_ray = vec3(1.0, 0.38, 0.08) * total_ray_red * 2.4;
        vec3 colored_rays = col_blue_ray + col_red_ray;

        // White light illuminates the ring as the circle closes
        float whiteOvertake = clamp(u_white_intensity * 1.2, 0.0, 1.0);
        vec3 white_ring_light = vec3(ring_intensity_blue + ring_intensity_red) * 2.2;
        vec3 ray_final = mix(colored_rays, white_ring_light, whiteOvertake * 0.7);

        // -------------------------------------------------------------
        // 2. Pure Glowing White Circle Formation
        // -------------------------------------------------------------
        float circleCore = 0.026 / (dist_ring * dist_ring * 20.0 + 0.026);
        float circleGlow = 0.060 / (dist_ring + 0.060);
        float pureCircleMark = (circleCore * 1.5 + circleGlow * 0.6) * clamp(u_white_intensity, 0.0, 1.0);

        // -------------------------------------------------------------
        // 3. Inward Spiral Vortex: Colors Move Inside & Spin with Scroll
        // -------------------------------------------------------------
        vec3 inwardRgb = vec3(0.0);
        if (u_inward_progress > 0.001) {
          float insideCircle = smoothstep(R_circle + 0.03, R_circle - 0.02, r_len);

          // Inward wavefront advances from R_circle down to center (0.015)
          float r_wave_front = mix(R_circle, 0.015, pow(u_inward_progress, 0.88));
          float inwardWaveMask = smoothstep(r_wave_front - 0.05, r_wave_front + 0.06, r_len) * insideCircle;

          // Two intertwined spiral arms winding into center, rotation driven by scroll!
          float spiral_inward_k = 2.3;
          float swirl_spin = u_scroll_spin; // Directly matches user scrolling down or up

          // Blue inward spiral arm
          float phi_in_exp_blue = phi_BL_contact + swirl_spin - spiral_inward_k * log(max(r_len / R_circle, 0.005));
          float d_phi_in_blue = abs(mod(phi - phi_in_exp_blue + 3.14159265, 6.2831853) - 3.14159265);

          // Red inward spiral arm (180 deg symmetric counterpart)
          float phi_in_exp_red = phi_in_exp_blue + 3.14159265;
          float d_phi_in_red = abs(mod(phi - phi_in_exp_red + 3.14159265, 6.2831853) - 3.14159265);

          float in_arm_w = 0.042 + 0.065 * (r_len / R_circle);
          float in_wave = sin(r_len * 12.0 - u_scroll_spin * 0.5) * 0.025;

          float dist_in_blue = r_len * abs(d_phi_in_blue + in_wave);
          float dist_in_red = r_len * abs(d_phi_in_red - in_wave);

          float in_intensity_blue = exp(-pow(dist_in_blue / in_arm_w, 2.0)) * inwardWaveMask;
          float in_intensity_red = exp(-pow(dist_in_red / in_arm_w, 2.0)) * inwardWaveMask;

          vec3 col_in_blue = vec3(0.12, 0.68, 1.0) * in_intensity_blue * 2.5;
          vec3 col_in_red = vec3(1.0, 0.36, 0.08) * in_intensity_red * 2.5;

          // Vortex luminous center
          float vortexCore = 0.015 / (r_len * r_len * 24.0 + 0.020) * u_inward_progress;
          vec3 col_vortex_center = vec3(0.5, 0.80, 1.0) * vortexCore * 1.2;

          inwardRgb = col_in_blue + col_in_red + col_vortex_center;
        }

        // -------------------------------------------------------------
        // 4. White Color Expands Outward Covering Outer Canvas
        // -------------------------------------------------------------
        float outerDist = max(0.0, r_len - R_circle);
        float isOuter = smoothstep(R_circle - 0.02, R_circle + 0.04, r_len);
        float outerWhiteFill = 0.0;
        if (u_white_spread > 0.001) {
          float fillFront = u_white_spread * 2.7;
          outerWhiteFill = smoothstep(fillFront + 0.45, fillFront - 0.2, outerDist) * u_white_spread * 0.90 * isOuter;
        }

        vec3 whiteOuter = vec3(pureCircleMark + outerWhiteFill);
        vec3 finalRgb = inwardRgb + ray_final + whiteOuter;
        finalRgb = clamp(finalRgb, 0.0, 1.0);

        // -------------------------------------------------------------
        // 5. Center Black Void Expanding & Pushing White Circle Outward
        // -------------------------------------------------------------
        float canvasDissolve = 1.0;
        if (u_dark_core > 0.001) {
          // Black hole expands from center (0.0) outwards past circle (0.72) and outer canvas
          float darkRadius = pow(u_dark_core, 1.1) * 2.6;
          float darkVoidMask = smoothstep(darkRadius - 0.05, darkRadius + 0.07, r_len);

          // Everything inside darkRadius is swallowed into pitch black darkness:
          // As darkRadius expands past R_circle (0.72), it physically pushes and swallows
          // the white circle and outer white glow outward!
          finalRgb = finalRgb * darkVoidMask;

          // In the dark wake, subtle tiny white stardust lights twinkle into background grid
          vec2 starCoord = sin(p_scaled * 54.0);
          float starDot = smoothstep(0.975, 1.0, starCoord.x * starCoord.y);
          float starTwinkle = 0.6 + 0.4 * sin(t * 4.5 + p_scaled.x * 25.0 + p_scaled.y * 35.0);
          float clearedVoidArea = (1.0 - darkVoidMask) * smoothstep(0.08, 0.35, darkRadius);
          vec3 tinyWhiteLights = vec3(1.0) * starDot * starTwinkle * clearedVoidArea * 0.85;
          finalRgb += tinyWhiteLights;

          // When dark void has swallowed the canvas, dissolve to transparent to reveal #dark-grid-canvas
          canvasDissolve = 1.0 - smoothstep(0.65, 0.98, u_dark_core);
        }

        // Alpha channel: blends seamlessly into dark site & background dot-grid
        float alpha = clamp(max(max(finalRgb.r, finalRgb.g), finalRgb.b) * 1.5, 0.0, 1.0) * canvasDissolve;
        gl_FragColor = vec4(finalRgb, alpha);
      }
    `;

    this.program = this.createProgram(vsSource, fsSource);
    if (!this.program) return;

    gl.useProgram(this.program);

    this.positionLocation = gl.getAttribLocation(this.program, 'a_position');
    this.timeLocation = gl.getUniformLocation(this.program, 't');
    this.resolutionLocation = gl.getUniformLocation(this.program, 'r');
    this.centerLocation = gl.getUniformLocation(this.program, 'u_center');
    this.scaleLocation = gl.getUniformLocation(this.program, 'u_scale');

    this.rayProgressLocation = gl.getUniformLocation(this.program, 'u_ray_progress');
    this.whiteIntensityLocation = gl.getUniformLocation(this.program, 'u_white_intensity');
    this.inwardProgressLocation = gl.getUniformLocation(this.program, 'u_inward_progress');
    this.whiteSpreadLocation = gl.getUniformLocation(this.program, 'u_white_spread');
    this.darkCoreLocation = gl.getUniformLocation(this.program, 'u_dark_core');
    this.scrollSpinLocation = gl.getUniformLocation(this.program, 'u_scroll_spin');

    // Full-screen quad
    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(this.positionLocation);
    gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Alpha blending
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Resize and listeners
    this.resize();
    window.addEventListener('resize', this.boundResize, { passive: true });

    // Intersection observer for viewport performance
    this.initObserver();
  }

  createShader(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('SpiralCosmicScene Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  createProgram(vsSource, fsSource) {
    const gl = this.gl;
    const vs = this.createShader(gl.VERTEX_SHADER, vsSource);
    const fs = this.createShader(gl.FRAGMENT_SHADER, fsSource);
    if (!vs || !fs) return null;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('SpiralCosmicScene Program link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  resize() {
    if (!this.canvas || !this.gl) return;
    const rect = this.canvas.parentElement
      ? this.canvas.parentElement.getBoundingClientRect()
      : this.canvas.getBoundingClientRect();
    const width = Math.max(300, Math.floor(rect.width));
    const height = Math.max(300, Math.floor(rect.height));

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const targetW = Math.floor(width * dpr);
    const targetH = Math.floor(height * dpr);

    if (this.canvas.width !== targetW || this.canvas.height !== targetH) {
      this.canvas.width = targetW;
      this.canvas.height = targetH;
      this.gl.viewport(0, 0, targetW, targetH);
    }
  }

  setScrollState({
    rayProgress,
    whiteIntensity,
    inwardProgress,
    whiteSpread,
    darkCore,
    scrollSpin,
  } = {}) {
    if (typeof rayProgress === 'number') this.rayProgress = Math.max(0, Math.min(1, rayProgress));
    if (typeof whiteIntensity === 'number')
      this.whiteIntensity = Math.max(0, Math.min(1, whiteIntensity));
    if (typeof inwardProgress === 'number')
      this.inwardProgress = Math.max(0, Math.min(1, inwardProgress));
    if (typeof whiteSpread === 'number') this.whiteSpread = Math.max(0, Math.min(1, whiteSpread));
    if (typeof darkCore === 'number') this.darkCore = Math.max(0, Math.min(1, darkCore));
    if (typeof scrollSpin === 'number') this.scrollSpin = scrollSpin;
  }

  updateUniforms() {
    if (!this.canvas || !this.gl || !this.program) return;
    const gl = this.gl;
    // Dead-center on the screen
    const centerX = this.canvas.width * 0.5;
    const centerY = this.canvas.height * 0.5;

    if (this.centerLocation) {
      gl.uniform2f(this.centerLocation, centerX, centerY);
    }
    if (this.scaleLocation) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      const scale = isMobile ? 1.45 : 1.78;
      gl.uniform1f(this.scaleLocation, scale);
    }

    if (this.rayProgressLocation) gl.uniform1f(this.rayProgressLocation, this.rayProgress);
    if (this.whiteIntensityLocation) gl.uniform1f(this.whiteIntensityLocation, this.whiteIntensity);
    if (this.inwardProgressLocation) gl.uniform1f(this.inwardProgressLocation, this.inwardProgress);
    if (this.whiteSpreadLocation) gl.uniform1f(this.whiteSpreadLocation, this.whiteSpread);
    if (this.darkCoreLocation) gl.uniform1f(this.darkCoreLocation, this.darkCore);
    if (this.scrollSpinLocation) gl.uniform1f(this.scrollSpinLocation, this.scrollSpin);
  }

  initObserver() {
    const target = this.canvas.parentElement || this.canvas;
    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      this.start();
      return;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.start();
          } else {
            this.stop();
          }
        });
      },
      { threshold: 0.05 },
    );
    observer.observe(target);
  }

  start() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.render();
  }

  stop() {
    this.isVisible = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  render() {
    if (!this.isVisible || !this.gl || !this.program) return;

    const gl = this.gl;
    const currentTime =
      typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now();
    const delta = (currentTime - this.startTime) / 1000;

    gl.useProgram(this.program);
    gl.uniform1f(this.timeLocation, delta);
    gl.uniform2f(this.resolutionLocation, this.canvas.width, this.canvas.height);
    this.updateUniforms();

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    this.animationFrameId = requestAnimationFrame(() => this.render());
  }

  destroy() {
    this.stop();
    window.removeEventListener('resize', this.boundResize);
    if (this.gl && this.buffer) {
      this.gl.deleteBuffer(this.buffer);
    }
    if (this.gl && this.program) {
      this.gl.deleteProgram(this.program);
    }
  }
}
